import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CheckCircle2, ChevronRight, ListChecks, Loader2, Search, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrandMark, VendorAvatar } from "@/components/Brand";
import { LogoutButton } from "@/components/LogoutButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function ProfileDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { customer, reload } = useAuth();
  const p = customer?.profile || {};
  const [form, setForm] = useState({ name: p.name || "", phone: p.phone || "", partner_name: p.partner_name || "", preferences: p.preferences || "" });
  const save = async () => {
    try {
      await api.put("/me/profile", form);
      await reload("customer");
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const field = (key: keyof typeof form, label: string) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`profile-${key}-input`} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="customer-profile-dialog">
        <DialogHeader><DialogTitle>Your details</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Optional extras are shared only with vendors you've already consented to.</p>
        {field("name", "Name")}
        {field("phone", "Phone")}
        {field("partner_name", "Partner / household member")}
        {field("preferences", "Preferences (e.g. budget, style)")}
        <Button onClick={save} className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700" data-testid="profile-save-button">Save</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function MyVendors() {
  const { customer } = useAuth();
  const [items, setItems] = useState<any[] | null>(null);
  const [q, setQ] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    if (customer) api.get("/me/vendors").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [customer]);

  if (customer === undefined) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!customer) return <Navigate to="/attendee" replace />;
  const filtered = (items || []).filter((i) => `${i.vendor.name} ${i.event_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3">
          <BrandMark />
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => setProfileOpen(true)} data-testid="customer-profile-button"><UserRound className="h-5 w-5" /></Button>
            <LogoutButton kind="customer" testid="customer-logout-button" />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-md space-y-5 px-4 py-6">
        <div className="rise">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Hi {customer.user.name?.split(" ")[0]}</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight">My vendors</h1>
        </div>
        <Link to="/me/profiles" className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="my-event-profiles-link">
          <ListChecks className="h-5 w-5 text-emerald-700" />
          <span className="flex-1"><span className="block text-sm font-bold">My event profiles</span><span className="block text-xs text-slate-600">Answer once, share instantly at every booth you scan</span></span>
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </Link>
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendor or event" className="h-11 pl-9" data-testid="my-vendors-search-input" />
        </div>
        {items === null && <Loader2 className="mx-auto h-5 w-5 animate-spin" />}
        {items?.length === 0 && <p className="rounded-2xl border border-dashed p-8 text-center text-sm text-slate-500" data-testid="my-vendors-empty">Your account is ready. At the event, scan any vendor's booth QR code and they'll be saved here instantly, no forms to re-type.</p>}
        <div className="space-y-3">
          {filtered.map((i, idx) => (
            <Link key={i.lead_id} to={`/me/v/${i.lead_id}`} data-testid={`my-vendor-card-${i.lead_id}`}
              className="rise hard-shadow-hover flex items-center gap-4 rounded-2xl border bg-white p-4" style={{ animationDelay: `${idx * 0.04}s` }}>
              <VendorAvatar vendor={i.vendor} size={48} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{i.vendor.name}</p>
                <p className="truncate font-mono text-[11px] uppercase tracking-wider text-slate-500">{i.event_name}</p>
                {i.quote_submitted && <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Quote sent</span>}
                {i.preregistered && <span className="ml-2 mt-1 inline-flex rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-slate-600" data-testid={`my-vendor-prereg-${i.lead_id}`}>{i.checked_in ? "Checked in" : "Registered"}</span>}
              </div>
              <ChevronRight className="h-5 w-5 text-slate-400" />
            </Link>
          ))}
        </div>
      </main>
      {profileOpen && <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />}
    </div>
  );
}
